import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronDown, GitCommitHorizontal } from 'lucide-react';
import { STAGE_META } from '../../lib/journeySignals';

const ORDER = ['capability', 'visibility', 'confidence', 'coordination', 'value', 'navigation'];

function Connector({ constraining }) {
  return (
    <div className="flex flex-col items-center justify-center px-1 flex-shrink-0">
      <ChevronDown className={`h-3.5 w-3.5 rotate-[-90deg] ${constraining ? 'text-rose-300' : 'text-slate-300'}`} />
      <span className={`text-[9px] font-semibold uppercase tracking-wider ${constraining ? 'text-rose-500' : 'text-slate-400'}`}>
        {constraining ? 'constraining' : 'contributes'}
      </span>
    </div>
  );
}

/**
 * Signal Propagation — shows movement traveling through the journey
 * chain: each stage's % change over the loaded window, with connectors
 * flagged "constraining" when the downstream stage is declining.
 * Same trend data as the graph — just chained.
 */
export function SignalPropagationChain({ stageHealth }) {
  if (!stageHealth?.navigation) return null;

  return (
    <Card data-testid="signal-propagation-chain">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <GitCommitHorizontal className="h-4 w-4 text-cyan-700" />
          Signal Propagation
        </CardTitle>
        <p className="text-xs text-slate-500">
          How movement travels through the journey — same trend data, chained from capability through to value.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-stretch overflow-x-auto gap-0.5 pb-1">
          {ORDER.map((key, i) => {
            const s = stageHealth[key];
            const meta = STAGE_META[key];
            const ch = s?.changePct;
            return (
              <React.Fragment key={key}>
                {i > 0 && <Connector constraining={ch != null && ch < 0} />}
                <div
                  className={`flex-1 min-w-[112px] rounded-lg border px-3 py-2.5 text-center ${
                    key === 'navigation' ? 'border-cyan-200 bg-cyan-50/60' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`propagation-stage-${key}`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{meta.label}</p>
                  <p className={`text-lg font-extrabold tabular-nums mt-0.5 ${
                    ch == null ? 'text-slate-400' : ch > 0 ? 'text-emerald-600' : ch < 0 ? 'text-rose-600' : 'text-slate-500'
                  }`}>
                    {ch == null ? '—' : `${ch > 0 ? '↑ +' : ch < 0 ? '↓ −' : '→ '}${Math.abs(ch).toFixed(1)}%`}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {s ? `${Math.round(s.latest)}% now` : 'no signals'}
                  </p>
                  <div className="h-1 mt-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(100, Math.max(0, s?.latest ?? 0))}%`, backgroundColor: meta.color }}
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
